import type { ZonaCatalogo } from '../api/ops/misReservas';
import type { Canal } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import type { NombreIcono } from '../componentes/iconos';
import { D } from './textos';

/** Nombre del canal dentro de una frase («por email», «por WhatsApp»). */
export function nombreCanal(c: Canal): string {
    switch (c) {
        case 'email':
            return 'email';
        case 'whatsapp':
            return 'WhatsApp';
        case 'instagram':
            return 'Instagram';
        case 'telegram':
            return 'Telegram';
        case 'sms':
            return 'SMS';
        case 'voice':
            return 'llamada';
    }
}

/** Nombre del canal suelto (opciones de un selector). */
export const etiquetaCanal = (c: Canal): string => (c === 'email' ? 'Email' : c === 'voice' ? 'Llamada' : nombreCanal(c));

export function iconoCanal(c: Canal): NombreIcono {
    switch (c) {
        case 'email':
            return 'mail';
        case 'whatsapp':
            return 'message-circle';
        case 'instagram':
            return 'instagram';
        case 'telegram':
            return 'send';
        case 'sms':
            return 'message-square-text';
        case 'voice':
            return 'phone';
    }
}

/** «+34612345678» → «+34 612 345 678» (España); el resto, tal cual. */
export function telefonoLegible(e164: string): string {
    const m = /^\+34(\d{3})(\d{3})(\d{3})$/.exec(e164.replace(/\s/g, ''));
    return m === null ? e164 : `+34 ${m[1] ?? ''} ${m[2] ?? ''} ${m[3] ?? ''}`;
}

export const emailValido = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
export const telefonoValido = (v: string): boolean => v.replace(/\D/g, '').length >= 9;

export const inicial = (nombre: string): string => (nombre.trim().charAt(0) || '·').toUpperCase();
export const nombreDePila = (nombre: string): string => nombre.trim().split(/\s+/)[0] ?? nombre;

/** «3 mar 2026». */
export const fechaCorta = (iso: string): string =>
    new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');
/** «9 nov». */
export const diaYMes = (iso: string): string => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '');
/** «sábado 17 de octubre, 21:30». */
export function fechaLarga(iso: string): string {
    const d = new Date(iso);
    const dia = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '');
    return `${dia}, ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
}
/** «21 de octubre». */
export const diaDelMes = (iso: string): string => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
export const hora = (iso: string): string => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

/** «9:55». */
export function minutosYSegundos(segundos: number): string {
    const s = Math.max(0, Math.round(segundos));
    return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

/** Error de un código (G153): el motivo de `errors[0].code` y los intentos que quedan. */
export type ErrorCodigo = { tipo: 'incorrecto'; quedan: number } | { tipo: 'caducado' } | { tipo: 'agotado' } | { tipo: 'aviso'; texto: string };

export function errorDeCodigo(e: unknown): ErrorCodigo | null {
    if (!(e instanceof ErrorApi) || e.codigo !== 'code_invalid') {
        return null;
    }
    const motivo = e.errores[0]?.code;
    const quedan = (e.problema as { attempts_remaining?: number | null }).attempts_remaining;
    if (motivo === 'expired') {
        return { tipo: 'caducado' };
    }
    if (motivo === 'attempts_exhausted' || quedan === 0) {
        return { tipo: 'agotado' };
    }
    return { tipo: 'incorrecto', quedan: quedan ?? 0 };
}

export const textoErrorCodigo = (e: ErrorCodigo): string =>
    e.tipo === 'incorrecto' ? D.incorrecto(e.quedan) : e.tipo === 'caducado' ? D.caducadoCodigo : e.tipo === 'agotado' ? D.agotado : e.texto;

export const esCodigo = (e: unknown, codigo: string): boolean => e instanceof ErrorApi && e.codigo === codigo;

/** Texto para un error que la pantalla no trata aparte. */
export function textoDeError(e: unknown): string {
    if (esCodigo(e, 'rate_limited')) {
        return D.demasiadas;
    }
    if (esCodigo(e, 'captcha_required')) {
        return D.captcha;
    }
    return e instanceof ErrorApi && e.estado < 500 && e.problema.detail !== undefined ? e.problema.detail : D.errorGeneral;
}

/** «Mis mensajes» (5.7): cuántas veces al día se puede pedir otra vez un mensaje (G44). */
export const REENVIOS_AL_DIA = 3;

/** «Puedes pedirlo 3 veces al día», «2 veces más hoy», «1 vez más hoy» o «Ya lo has pedido 3 veces hoy». */
export function textoReenvios(quedan: number): string {
    if (quedan >= REENVIOS_AL_DIA) {
        return 'Puedes pedirlo 3 veces al día';
    }
    if (quedan <= 0) {
        return 'Ya lo has pedido 3 veces hoy. Mañana podrás otra vez.';
    }
    return `Puedes pedirlo ${String(quedan)} ${quedan === 1 ? 'vez' : 'veces'} más hoy`;
}

/** 8.9 · Horas de entrada y de salida del alojamiento junto al calendario (G797; las da la zona del catálogo). */
export function horasEstancia(zona: Pick<ZonaCatalogo, 'check_in_local_time' | 'check_out_local_time'>): string {
    const entrada = zona.check_in_local_time ?? '';
    const salida = zona.check_out_local_time ?? '';
    if (entrada === '') {
        return '';
    }
    return salida === '' ? `Entrada desde las ${entrada}` : `Entrada desde las ${entrada} · salida hasta las ${salida}`;
}
